import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertTriangle, BarChart3, CalendarCheck, Clock, ListTodo, Loader2, Timer } from 'lucide-react';

const BUCKET_NOUN = { week: 'day', month: 'week', quarter: 'month', year: 'month' };
const formatHours = (value) => `${Number(value || 0).toFixed(1)}h`;

/** Consolidated monthly / quarterly / yearly timesheet from GET /timesheets/summary. */
const TimesheetSummaryView = ({ summary, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!summary) {
    return (
      <Card className="border-border">
        <CardContent className="p-12 text-center text-sm text-muted-foreground">
          Could not load the timesheet summary.
        </CardContent>
      </Card>
    );
  }

  const maxBucketHours = Math.max(...summary.buckets.map(b => b.hours), 1);
  const stats = [
    { label: 'Total Hours', value: summary.total_hours.toFixed(1), icon: Clock },
    { label: 'Days Logged', value: `${summary.days_logged}/${summary.working_days}`, icon: CalendarCheck },
    { label: 'Avg Hrs/Day', value: summary.avg_hours_per_logged_day.toFixed(1), icon: Timer },
    {
      label: 'Missed Days',
      value: summary.missed_days,
      icon: AlertTriangle,
      className: summary.missed_days > 0 ? 'text-orange-500' : 'text-green-500',
    },
  ];

  return (
    <div className="space-y-6" data-testid={`timesheet-summary-${summary.period}`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        {stats.map(stat => (
          <Card key={stat.label} className="border-border bg-muted/30">
            <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="h-10 w-10 bg-primary/10 hidden sm:flex items-center justify-center shrink-0">
                <stat.icon className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className={`text-xl sm:text-2xl font-bold ${stat.className || ''}`}>{stat.value}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            HOURS BY {BUCKET_NOUN[summary.period].toUpperCase()}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {summary.buckets.map(bucket => (
            <div key={bucket.key} className="grid grid-cols-[minmax(0,9rem)_1fr_3.5rem] sm:grid-cols-[14rem_1fr_4rem] items-center gap-3 text-xs">
              <span className="truncate text-muted-foreground" title={bucket.label}>{bucket.label}</span>
              <div className="h-2.5 bg-muted overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${(bucket.hours / maxBucketHours) * 100}%` }} />
              </div>
              <span className="text-right font-mono">{formatHours(bucket.hours)}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <ListTodo className="h-4 w-4" />
            HOURS BY TASK
          </CardTitle>
        </CardHeader>
        <CardContent>
          {summary.by_task.length > 0 ? (
            <div className="space-y-2">
              {summary.by_task.map(item => (
                <div key={item.task_id || 'none'} className="flex items-center justify-between gap-3 p-3 border border-border">
                  <span className="text-sm truncate">{item.title}</span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {item.entries} entr{item.entries === 1 ? 'y' : 'ies'} · <span className="font-mono font-medium text-foreground">{formatHours(item.hours)}</span>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No timesheet entries in this period</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TimesheetSummaryView;
